import { find, isNil } from 'lodash';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../auth/UserContext';
import { Membership } from '../auth/api';
import { DEEP_LINK_UI_QUERY_PARAM_KEY } from '../deepLink/constants';
import { useIsAppAccessGranted } from '../utils/commonUtils';
import {
    ADMINISTRATION,
    APPLICATIONS,
    AppDefinition,
    PRODUCTION_CONNECT,
    PROFILES,
    RESERVATION_PROFESSIONAL
} from './apps';

export default (selectedOrgId?: string) => {
    const { userProfile, activeOrganizationAccount, application } = useContext(UserContext);
    const location = useLocation();

    const buildSearchWithOrgId = (orgId: string): string => {
        const currentSearchParams = new URLSearchParams(location.search);
        const nextSearchParams = new URLSearchParams({ orgId });
        const uiParam = currentSearchParams.get(DEEP_LINK_UI_QUERY_PARAM_KEY);

        if (uiParam) {
            nextSearchParams.set(DEEP_LINK_UI_QUERY_PARAM_KEY, uiParam);
        }

        return `?${nextSearchParams.toString()}`;
    };

    const reservartionProfessionalAccess = useIsAppAccessGranted(
        userProfile,
        activeOrganizationAccount,
        RESERVATION_PROFESSIONAL
    );
    const productionConnectAccess = useIsAppAccessGranted(userProfile, activeOrganizationAccount, PRODUCTION_CONNECT);

    if (!isNil(userProfile)) {
        // user not sunscribed to any accounts
        if (userProfile.memberships?.length === 1 && userProfile.memberships[0].personal === true) {
            const otherAppAccess = userProfile.memberships[0].access?.find(
                (a) => a.application.toLowerCase() !== PROFILES.id
            );

            if (otherAppAccess) {
                // match the access.application string with APPLICATIONS.name
                const app = APPLICATIONS.find((appDef) => appDef.name === otherAppAccess.application);

                const previousAppPreview = app.home !== application?.home;

                if (previousAppPreview) {
                    const url = `${app.home}${buildSearchWithOrgId(userProfile.memberships[0].id)}`;
                    return { url, pathname: app.home, app, orgId: userProfile.memberships[0].id };
                }
            }
            // redirect to profiles if no other app access found
            const url = `${PROFILES.home}${buildSearchWithOrgId(userProfile.memberships[0].id)}`;
            return { url, pathname: PROFILES.home, app: PROFILES, orgId: userProfile.memberships[0].id };
        } else {
            // Find membership by id or first non-personal one.
            const membership = find(userProfile.memberships, (m: Membership) => {
                if (selectedOrgId) {
                    return !m.personal && m.id === selectedOrgId;
                }
                // Return first membership that is not personal and has any access defined on it.
                return !m.personal && Array.isArray(m.access);
            });

            if (membership?.access?.length) {
                const preferredAppKey = 'preferredApp.' + userProfile.accountId;
                const preferedAppId = localStorage.getItem(preferredAppKey);

                // Check if preferred app exists AND membership has access to it
                if (preferedAppId) {
                    const preferredApp = find(APPLICATIONS, { id: preferedAppId });
                    // Does membership access include this preferred app?
                    const hasPreferredAppAccess = membership.access.some((a) => a.application === preferredApp?.name);

                    if (preferredApp && hasPreferredAppAccess) {
                        const url = `${preferredApp.home}${buildSearchWithOrgId(membership.id)}`;
                        return { url, pathname: preferredApp.home, app: preferredApp, orgId: membership.id };
                    }
                }

                // Fallback: find accessible apps for membership
                const uniqueAppNames = Array.from(new Set(membership.access.map((a) => a.application)));
                const accessibleApps = uniqueAppNames
                    .map((name) => find(APPLICATIONS, { name }))
                    .filter(Boolean) as AppDefinition[];

                if (accessibleApps.length) {
                    const chosenApp =
                        // If more than one app and at least one is Administration
                        accessibleApps.length > 1 && accessibleApps.some((app) => app.name === ADMINISTRATION.name)
                            ? // Pick the first app that is NOT Administration
                              accessibleApps.find((app) => app.name !== ADMINISTRATION.name)
                            : // Otherwise, pick the first available app
                              accessibleApps[0];

                    if (chosenApp) {
                        let pathname = chosenApp.home;
                        let search = buildSearchWithOrgId(membership.id);

                        if (location.pathname.includes('/supplier/details/') && reservartionProfessionalAccess) {
                            pathname = location.pathname.replace('/supplier/', '/reservationProfessional/');
                            search = location.search;
                        }

                        if (location.pathname.includes('/planning/details/') && productionConnectAccess) {
                            pathname = location.pathname.replace('/planning/', '/productionConnect/');
                            search = location.search;
                        }

                        return {
                            url: `${pathname}${search}`,
                            pathname,
                            app: chosenApp,
                            orgId: membership.id
                        };
                    }
                }
            } else {
                if (userProfile.memberships?.length && userProfile.memberships[0].personal === true) {
                    const url = `${PROFILES.home}${buildSearchWithOrgId(userProfile.memberships[0].id)}`;
                    return { url, pathname: PROFILES.home, app: PROFILES, orgId: userProfile.memberships[0].id };
                }
            }
        }
    }
    return { url: null, pathname: null, app: null, orgId: null };
};
